import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { useScrollRestoration } from '@redpanda/utils';
import AccountListPage from './pages/AccountListPage';
import JournalListPage from './pages/JournalListPage';
import InvoiceManagePage from './pages/InvoiceManagePage';
import ReportsPage from './pages/ReportsPage';
import DashboardPage from './pages/DashboardPage';
import PeriodManagementPage from './pages/PeriodManagementPage';
import PeriodComparisonPage from './pages/PeriodComparisonPage';
import ProfilePage from './pages/ProfilePage';
import BankAccountPage from './pages/BankAccountPage';
import BankTransactionPage from './pages/BankTransactionPage';
import BankReconciliationPage from './pages/BankReconciliationPage';
import TaxReportPage from './pages/TaxReportPage';
import AdminPage from './pages/AdminPage';
import ManualPage from './pages/ManualPage';
import DataIOPage from './pages/DataIOPage';
import { UserProvider, UserContext } from './context/UserContext';
import { ColorThemeProvider } from './context/ColorThemeContext';
import { FAQPage, TermsOfServicePage, PrivacyPolicyPage, MobileTopBar } from '@redpanda/ui';
import { Role } from '@redpanda/types';
import PrivateRoute from './components/PrivateRoute';
import Sidebar from './components/Sidebar';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';

/** 捲動管理（共用 useScrollRestoration）：重新整理、上一頁、麵包屑回列表時還原捲動位置，進新頁面捲回頂端 */
const ScrollManager: React.FC = () => {
  useScrollRestoration(useLocation(), useNavigationType());
  return null;
};

// 使用手冊依「上次停留的頁面」捲到對應章節（ManualPage）
const SYSTEM_PATHS = ['/accounts', '/journals', '/bank-reconciliation', '/invoices', '/tax-reports', '/bank-accounts', '/reports', '/period-management', '/period-comparison', '/data-io'];

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useContext(UserContext)!;
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = `RedPanda ${t('app.title')}`;
  }, [t]);

  useEffect(() => {
    const active = location.pathname === '/' ? '/' : SYSTEM_PATHS.find(path => location.pathname.startsWith(path));
    if (active) sessionStorage.setItem('lastActivePage', active);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="app-loading bg-white dark:bg-slate-950 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="RedPanda Tech"
            className="w-48 h-48 mx-auto mb-6 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('.logo-fallback')!.classList.remove('hidden');
            }}
          />
          <div className="logo-fallback hidden text-2xl font-semibold text-gray-900 dark:text-white mb-6">RedPanda {t('app.title')}</div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex">
      <Toaster position="bottom-right" />
      <ScrollManager />

      {isAuthenticated && (
        <Sidebar
          className="w-48 h-screen fixed left-0 top-0 z-50"
          isMobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div className={`flex-1 min-w-0 flex flex-col ${isAuthenticated ? 'md:ml-48' : ''}`}>
        {/* 手機版頂部列（R27）：取代原本浮動在左上角、會蓋住頁標題的選單按鈕 */}
        {isAuthenticated && (
          <MobileTopBar
            title={`RedPanda ${t('app.title')}`}
            onOpenMenu={() => setIsMobileSidebarOpen(true)}
            menuLabel={t('common.navigation.openMenu')}
          />
        )}

        <main className="flex-grow">
          <Routes>
            <Route path="/manual" element={<ManualPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            <Route element={<PrivateRoute />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/accounts" element={<AccountListPage />} />
              <Route path="/journals" element={<JournalListPage />} />
              <Route path="/invoices" element={<InvoiceManagePage />} />
              <Route path="/invoices/archived" element={<InvoiceManagePage />} />
              <Route path="/bank-accounts" element={<BankAccountPage />} />
              <Route path="/bank-accounts/:accountId" element={<BankTransactionPage />} />
              <Route path="/bank-reconciliation" element={<BankReconciliationPage />} />
              <Route path="/tax-reports" element={<TaxReportPage />} />
              <Route path="/period-management" element={<PeriodManagementPage />} />
              <Route path="/period-comparison" element={<PeriodComparisonPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            <Route element={<PrivateRoute allowedRoles={[Role.SUPER_ADMIN]} />}>
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/data-io" element={<DataIOPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <ColorThemeProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </ColorThemeProvider>
  </Router>
);

export default App;
